import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/app/appStore';
import { useSaveTemplate } from './useTemplates';

export type SaveTemplatePayload = {
  surface: string;
  config: unknown;
  originatingPrompt?: string | null;
};

type SaveTemplateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payload: SaveTemplatePayload | null;
};

export function SaveTemplateDialog({
  open,
  onOpenChange,
  payload,
}: SaveTemplateDialogProps) {
  const { t } = useTranslation('common');
  const save = useSaveTemplate();
  const setLastAgentAction = useAppStore((s) => s.setLastAgentAction);
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'shared'>('private');
  const [error, setError] = useState<string | null>(null);

  function handleClose(next: boolean) {
    if (!next) {
      setName('');
      setVisibility('private');
      setError(null);
    }
    onOpenChange(next);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!payload) return;
    if (!name.trim()) {
      setError(t('templates.nameLabel'));
      return;
    }
    setError(null);
    try {
      // Note: backend schema has no `visibility` field yet; we stash it in
      // the config envelope so the UI can read it back round-trip.
      const configWithMeta = {
        ...(typeof payload.config === 'object' && payload.config !== null
          ? (payload.config as Record<string, unknown>)
          : { value: payload.config }),
        _visibility: visibility,
      };
      await save.mutateAsync({
        name: name.trim(),
        surface: payload.surface,
        originating_prompt: payload.originatingPrompt ?? null,
        config: configWithMeta,
      });
      setLastAgentAction({
        correlationId: 'template-save',
        description: t('templates.saved'),
        timestamp: new Date().toISOString(),
      });
      handleClose(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templates.saveError'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-[20%] z-50 w-[92%] max-w-md -translate-x-1/2 rounded-lg border border-border bg-card p-5 text-card-foreground shadow-wb-lg"
          aria-describedby={undefined}
        >
          <Dialog.Title className="text-md font-semibold">
            {t('templates.saveTitle')}
          </Dialog.Title>
          <form onSubmit={handleSave} className="mt-4 space-y-4">
            <div className="space-y-1">
              <label
                htmlFor="template-name"
                className="block text-xs uppercase text-muted-foreground"
              >
                {t('templates.nameLabel')}
              </label>
              <input
                id="template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('templates.namePlaceholder')}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
            </div>

            <fieldset className="space-y-1">
              <legend className="text-xs uppercase text-muted-foreground">
                {t('templates.visibilityLabel')}
              </legend>
              <div className="flex gap-3 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="visibility"
                    value="private"
                    checked={visibility === 'private'}
                    onChange={() => setVisibility('private')}
                  />
                  {t('templates.visibilityPrivate')}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="visibility"
                    value="shared"
                    checked={visibility === 'shared'}
                    onChange={() => setVisibility('shared')}
                  />
                  {t('templates.visibilityShared')}
                </label>
              </div>
            </fieldset>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => handleClose(false)}
                className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
              >
                {t('templates.cancel')}
              </button>
              <button
                type="submit"
                disabled={save.isPending}
                className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {t('templates.save')}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
